import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { TAG_DELETE_ENDPOINT } from "../../../config/urls";
import { useEffect, useState } from "react";
import { Close } from "@mui/icons-material";
import { useAuth } from "../../../hooks/AuthProvider";
import axios from "axios";

const DeleteTagModal = ({ open, handleClose, selectedTag }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagName, setTagName] = useState(selectedTag?.name || "");

  const { getValidToken } = useAuth();

  const handleCancel = () => {
    setTagName("");
    setError(null);
    handleClose();
  };

  const deleteTag = async () => {
    setLoading(true);
    setError(null);

    if (!selectedTag) {
      setError("Tag not found");
      setLoading(false);
      return;
    }

    try {
      const apiUrl = TAG_DELETE_ENDPOINT;

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await axios.delete(
        `${apiUrl}${selectedTag.id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Error: " + response.data.error);
      }

      handleCancel();
    } catch (err) {
      if (err.response?.data) {
        if (typeof err.response.data === "object") {
          const firstError = Object.values(err.response.data)[0];
          if (Array.isArray(firstError)) {
            setError(firstError[0]);
          } else {
            setError(firstError);
          }
        } else {
          setError(err.response.data);
        }
      } else if (err.message) {
        setError("Failed to delete tag: " + err.message);
      } else {
        setError("Failed to delete tag.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTagName(selectedTag?.name);
  }, [selectedTag]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1204 }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Delete Tag
        <IconButton aria-label="close" onClick={handleCancel}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={1}>
          {error && (
            <Alert severity="error">{typeof error === "string" && error}</Alert>
          )}

          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete the tag{" "}
            <strong>"{tagName}"</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This tag is currently assigned to {selectedTag?.audienceCount}{" "}
            contacts. Deleting it will remove the tag from all contacts.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={deleteTag}
          variant="contained"
          color="error"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTagModal;
